import { useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { z } from 'zod'

import { suppliersApi } from '@/api/suppliers'
import { AppShell, ConnectionBadge } from '@/components/AppShell'
import { IconTruck } from '@/components/icons'
import { Alert, Button, EmptyState, Field, SkeletonList } from '@/components/ui'
import { useApiError } from '@/features/auth/useApiError'
import { describeDeliveryDays } from '@/features/delivery/days'
import { formatRelativeDay } from '@/lib/format'
import { useAuthStore } from '@/stores/authStore'

const schema = z.object({
  name: z.string().min(2),
  email: z.string().email(),
  phone: z.string().max(32).optional().or(z.literal('')),
})

type FormValues = z.infer<typeof schema>

export default function SuppliersPage() {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [isAdding, setIsAdding] = useState(false)
  const canManage = useAuthStore((s) => s.hasPermission('supplier:manage'))

  const suppliers = useQuery({
    queryKey: ['suppliers', search],
    queryFn: () => suppliersApi.list(search || undefined),
  })

  return (
    <AppShell
      title={t('nav.suppliers')}
      action={
        canManage && !isAdding ? { label: t('suppliers.add'), onClick: () => setIsAdding(true) } : null
      }
    >
      {isAdding && <AddSupplierForm onDone={() => setIsAdding(false)} />}

      <input
        type="search"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={t('suppliers.searchPlaceholder')}
        className="mb-4 h-touch w-full rounded-xl border border-gray-300 bg-white px-4 text-base text-gray-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-100"
      />

      {suppliers.isPending && <SkeletonList rows={4} />}

      {suppliers.data?.length === 0 && (
        <EmptyState
          icon={<IconTruck size={26} />}
          title={search ? t('suppliers.noResults') : t('suppliers.empty')}
          description={search ? undefined : t('suppliers.emptyHint')}
        />
      )}

      <ul className="flex flex-col gap-2">
        {suppliers.data?.map((supplier) => (
          <li key={supplier.id}>
            <Link
              to={`/suppliers/${supplier.id}`}
              className="flex items-center justify-between gap-3 rounded-2xl border border-gray-200 bg-white p-4 hover:border-brand-300"
            >
              <div className="min-w-0">
                <p className="truncate font-semibold text-gray-900">{supplier.name}</p>
                {/* Τι ενδιαφέρει πριν την παραγγελία: πότε παραδίδει. Το email
                    είναι στοιχείο διαχείρισης και μένει στην καρτέλα. */}
                <p className="truncate text-sm text-gray-500">
                  {supplier.delivery
                    ? t('delivery.deliversOn', {
                        days: describeDeliveryDays(supplier.delivery.deliveryDays, t, true),
                      })
                    : supplier.email}
                </p>
                <p className="mt-1 truncate text-xs text-gray-400">
                  {t('suppliers.itemCount', { count: supplier.itemCount })}
                  {supplier.delivery &&
                    ` · ${t('delivery.earliest')}: ${formatRelativeDay(supplier.delivery.earliestDate, t)}`}
                </p>
              </div>
              <ConnectionBadge status={supplier.connectionStatus} />
            </Link>
          </li>
        ))}
      </ul>
    </AppShell>
  )
}

function AddSupplierForm({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const translateError = useApiError()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) })

  const mutation = useMutation({
    mutationFn: suppliersApi.create,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['suppliers'] })
      onDone()
    },
  })

  return (
    <form
      className="mb-4 flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white p-4"
      onSubmit={handleSubmit((values) =>
        mutation.mutate({ ...values, phone: values.phone || undefined }),
      )}
      noValidate
    >
      <h2 className="font-semibold text-gray-900">{t('suppliers.add')}</h2>

      {mutation.isError && <Alert>{translateError(mutation.error)}</Alert>}

      <Field
        label={t('suppliers.fields.name')}
        autoFocus
        error={errors.name && t('suppliers.validation.name')}
        {...register('name')}
      />
      <Field
        label={t('suppliers.fields.email')}
        type="email"
        inputMode="email"
        hint={t('suppliers.emailIsIdentity')}
        error={errors.email && t('auth.validation.email')}
        {...register('email')}
      />
      <Field label={t('suppliers.fields.phone')} type="tel" inputMode="tel" {...register('phone')} />

      <div className="flex gap-2">
        <Button type="submit" loading={mutation.isPending}>
          {t('common.save')}
        </Button>
        <Button type="button" variant="secondary" onClick={onDone}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  )
}
